/**
 * Ride API Client
 * 
 * Client for calling UAT or Production APIs for ride operations:
 * - createride: Create rides in UAT/Production
 * - searchusers: Search for users by name
 * - searchlocations: Search for locations by address (fallback)
 * 
 * Handles authentication, retries, and error parsing.
 * 
 * NOTE: All external requests are proxied through the backend to avoid CORS issues
 * and keep provider keys out of the browser bundle.
 */

import { getAtlasApiBaseUrl } from './getAtlasApiBaseUrl';
import { atlasFetch } from './api/atlasBackendClient';
import { makeRequestWithRetry, createHeaders, type Environment, type UATNetworkSettingsResponse, fetchServiceSettings } from './fixedRouteUatClient';
import { extractUATServices } from './fixedRouteUatImporter';
import { getEnvironmentApiBaseUrl } from './environmentConfig';

/**
 * Normalize Portuguese characters in addresses for better search results
 * Based on the Python script's address normalization
 */
function normalizePortugueseAddress(address: string): string {
  if (!address || typeof address !== 'string') {
    return address;
  }

  // Portuguese character normalization mapping
  const PORTUGUESE_CHAR_MAPPING: Record<string, string> = {
    'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
    'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
    'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
    'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
    'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
    'ç': 'c', 'ñ': 'n',
    'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
    'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
    'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
    'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
    'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
    'Ç': 'C', 'Ñ': 'N'
  };

  // Common Portuguese address pattern replacements
  const ADDRESS_REPLACEMENTS: Record<string, string> = {
    'R. ': 'Rua ',
    'Av. ': 'Avenida ',
    'Al. ': 'Alameda ',
    'Tv. ': 'Travessa ',
    'Pç. ': 'Praça ',
    'Est. ': 'Estrada ',
    'Rod. ': 'Rodovia ',
    'BR-': 'BR ',
    'RS-': 'RS ',
    'SC-': 'SC ',
    'PR-': 'PR '
  };

  // Normalize characters
  let normalized = address;
  for (const [accented, base] of Object.entries(PORTUGUESE_CHAR_MAPPING)) {
    normalized = normalized.replace(new RegExp(accented, 'g'), base);
  }

  // Handle common Portuguese address patterns
  for (const [abbrev, full] of Object.entries(ADDRESS_REPLACEMENTS)) {
    normalized = normalized.replace(new RegExp(abbrev.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), full);
  }

  console.log(`[normalizePortugueseAddress] '${address}' → '${normalized}'`);
  return normalized;
}

/**
 * Determine region and language based on service area city
 * Defaults to Brasil (br/pt-BR) if city contains Brasil/Brazil indicators
 */
function getRegionAndLanguage(serviceAreaCity: string): { region: string; language: string } {
  const cityLower = serviceAreaCity.toLowerCase();
  
  // Check for Brasil/Brazil indicators
  if (cityLower.includes('brasil') || cityLower.includes('brazil') || 
      cityLower.includes('caxias') || cityLower.includes('rs') || 
      cityLower.includes('rio grande do sul')) {
    return { region: 'br', language: 'pt-BR' };
  }
  
  // Default to Australia for other regions
  return { region: 'au', language: 'en' };
}

// Backend API base URL
const BACKEND_API_BASE_URL = getAtlasApiBaseUrl();

/**
 * Get API base URL based on environment
 */
function getApiBaseUrl(environment: Environment = 'UAT'): string {
  return getEnvironmentApiBaseUrl(environment);
}

/**
 * Get backend proxy URL (same endpoint handles both UAT and Production)
 */
function getProxyUrl(): string {
  return `${BACKEND_API_BASE_URL}/api/uat/proxy`;
}

/**
 * Get API URLs for a specific environment
 */
function getApiUrls(environment: Environment = 'UAT') {
  const baseUrl = getApiBaseUrl(environment);
  return {
    baseUrl,
    opsApiUrl: `${baseUrl}/context/ops/q`,
    opsCreateUrl: `${baseUrl}/context/ops/c`,
  };
}

/**
 * User search result (from search API)
 */
export interface UserSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  displayId?: number;
}

/**
 * User list entry (from getuserlist API)
 */
export interface UserListEntry {
  id: string;
  displayId: number;
  status: string; // e.g., "approved", "pending"
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
  mobileCountryCode?: string;
  mobileCountryDialCode?: string;
  management?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

/**
 * User list response
 */
export interface UserListResponse {
  status: number;
  data: {
    totalEntries: number;
    entries: UserListEntry[];
  };
}

/**
 * Location search result
 */
export interface LocationSearchResult {
  latitude: number;
  longitude: number;
  placeId?: string;
  address: string;
  label?: string;
}

/**
 * Create ride response
 */
export interface CreateRideResponse {
  success: boolean;
  rideId?: string;
  error?: string;
  message?: string;
}

/**
 * Get list of users with pagination
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param offset - Pagination offset (default: 0)
 * @param limit - Number of users per page (default: 20)
 * @param sortBy - Field to sort by (default: "createdAt")
 * @param sortOrder - Sort order "ASC" or "DESC" (default: "DESC")
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to user list response
 */
export async function getUserList(
  jwtToken: string,
  productId: string,
  networkId: string,
  offset: number = 0,
  limit: number = 20,
  sortBy: string = 'createdAt',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  environment: Environment = 'UAT'
): Promise<UserListResponse> {
  const { opsApiUrl } = getApiUrls(environment);
  
  const params: Record<string, string> = {
    type: 'getuserlist',
    version: '1',
    resource: 'users',
    operation: 'list',
    aclContext: 'operations',
    sortBy,
    sortOrder,
    offset: offset.toString(),
    limit: limit.toString(),
  };

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    throw new Error(`Failed to get user list: HTTP ${response.status}`);
  }

  // Parse response structure
  const apiResponse = response.data;
  if (!apiResponse || !apiResponse.data) {
    return {
      status: response.status,
      data: {
        totalEntries: 0,
        entries: [],
      },
    };
  }

  return {
    status: response.status,
    data: {
      totalEntries: apiResponse.data.totalEntries || 0,
      entries: apiResponse.data.entries || [],
    },
  };
}

/**
 * Search for a user by name
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param userName - Name of the user to search for
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to user search results
 */
export async function searchUser(
  jwtToken: string,
  productId: string,
  networkId: string,
  userName: string,
  environment: Environment = 'UAT'
): Promise<UserSearchResult[]> {
  const { opsApiUrl } = getApiUrls(environment);
  
  const params: Record<string, string> = {
    type: 'search',
    version: '1',
    resource: 'users',
    operation: 'list',
    aclContext: 'operations',
    searchString: userName.toLowerCase(),
  };

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    throw new Error(`Failed to search user: HTTP ${response.status}`);
  }

  // Parse response structure
  const apiResponse = response.data;
  if (!apiResponse || !apiResponse.data) {
    return [];
  }

  const entries = apiResponse.data.entry || [];
  return entries.map((entry: any) => {
    const userData = entry.data || entry;
    return {
      id: userData.id,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email,
      displayId: userData.displayId,
    };
  });
}

/**
 * Get user by ID (UUID)
 * 
 * Searches through user list to find a user by their UUID.
 * This is useful when you have a user ID from a downloaded ride.
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param userId - User UUID to find
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to user if found, null otherwise
 */
export async function getUserById(
  jwtToken: string,
  productId: string,
  networkId: string,
  userId: string,
  environment: Environment = 'UAT'
): Promise<UserListEntry | null> {
  // Check if userId looks like a UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(userId)) {
    // Not a UUID, return null
    return null;
  }

  // Search through user list pages to find the user
  // We'll search up to 10 pages (200 users) to find the user
  const pageSize = 20;
  const maxPages = 10;
  
  for (let page = 0; page < maxPages; page++) {
    try {
      const offset = page * pageSize;
      const userList = await getUserList(
        jwtToken,
        productId,
        networkId,
        offset,
        pageSize,
        'createdAt',
        'DESC',
        environment
      );
      
      // Check if user is in this page
      const foundUser = userList.data.entries.find(u => u.id === userId);
      if (foundUser) {
        return foundUser;
      }
      
      // If we've reached the end of the list, stop searching
      if (userList.data.entries.length < pageSize) {
        break;
      }
    } catch (error) {
      console.warn(`[getUserById] Error fetching user list page ${page}:`, error);
      // Continue to next page
    }
  }
  
  // User not found in searched pages
  return null;
}

/**
 * Search for a location using Google Maps API (primary method)
 * Uses backend proxy to avoid CORS issues.
 * 
 * @param address - Address to search for
 * @param serviceAreaCity - Service area city for validation
 * @param region - Region bias (e.g., "br", "au")
 * @param language - Language preference (e.g., "pt-BR", "en")
 * @returns Promise resolving to location search result or null
 */
export async function searchLocationWithGoogleMaps(
  address: string,
  serviceAreaCity: string,
  region?: string,
  language?: string
): Promise<LocationSearchResult | null> {
  // Use backend proxy to avoid CORS issues
  const proxyUrl = `${BACKEND_API_BASE_URL}/api/google-maps/place-search`;
  
  // Determine region and language based on service area city if not provided
  const { region: determinedRegion, language: determinedLanguage } = getRegionAndLanguage(serviceAreaCity);
  const finalRegion = region || determinedRegion;
  const finalLanguage = language || determinedLanguage;
  
  // Normalize Portuguese address for better search results
  const normalizedAddress = normalizePortugueseAddress(address);
  
  // Try multiple search strategies (matching Python script)
  const searchStrategies = [
    // Strategy 1: Original address + explicit city context (most specific)
    { query: `${address}, ${serviceAreaCity}`, region: finalRegion, language: finalLanguage },
    // Strategy 2: Normalized address + explicit city context
    { query: `${normalizedAddress}, ${serviceAreaCity}`, region: finalRegion, language: finalLanguage },
    // Strategy 3: Original address with region bias
    { query: address, region: finalRegion, language: finalLanguage },
    // Strategy 4: Normalized address with region bias
    { query: normalizedAddress, region: finalRegion, language: finalLanguage },
  ];

  for (const strategy of searchStrategies) {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Call backend proxy instead of Google Maps directly
      const response = await atlasFetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: strategy.query,
          service_area_city: serviceAreaCity,
          region: strategy.region,
          language: strategy.language,
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn(`[searchLocationWithGoogleMaps] Backend proxy returned status: ${response.status}`);
        continue;
      }

      const data = await response.json();

      // Check if we got a successful result
      if (data.status === 'OK' && data.latitude !== null && data.longitude !== null) {
        console.log(`✅ Found location: ${data.latitude}, ${data.longitude}`);
        console.log(`✅ Address: ${data.address}`);
        
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          placeId: data.place_id || undefined,
          address: data.address || address,
          label: address,
        };
      } else {
        console.warn(`⚠️ Google Maps API returned status: ${data.status}`);
        if (data.error_message) {
          console.warn(`⚠️ Error message: ${data.error_message}`);
        }
        // Continue to next strategy
      }
    } catch (error) {
      console.warn('[searchLocationWithGoogleMaps] Error with strategy:', error);
      continue;
    }
  }

  return null;
}

/**
 * Search for a location using internal API (fallback method)
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param address - Address to search for
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to location search result or null
 */
export async function searchLocationWithInternalAPI(
  jwtToken: string,
  productId: string,
  networkId: string,
  address: string,
  environment: Environment = 'UAT'
): Promise<LocationSearchResult | null> {
  const { opsApiUrl } = getApiUrls(environment);
  
  // Try multiple search types (matching Python script)
  const searchTypes = ['searchlocations', 'search', 'geocode', 'locationsearch'];
  
  for (const searchType of searchTypes) {
    try {
      console.log(`🔍 Trying internal search type: ${searchType}`);
      
      const payload = {
        type: searchType,
        version: '1',
        resource: 'locations',
        operation: 'search',
        aclContext: 'operations',
        query: address,
        networkId: networkId,
        limit: 10,
      };

      const response = await makeRequestWithRetry(
        opsApiUrl,
        'POST',
        createHeaders(jwtToken, productId, environment),
        undefined,
        payload,
        3,
        1000,
        environment
      );

      if (response.status !== 200) {
        console.warn(`❌ Internal ${searchType} search failed with status: ${response.status}`);
        continue; // Try next search type
      }

      const apiResponse = response.data;
      if (!apiResponse || !apiResponse.data) {
        continue; // Try next search type
      }

      // Check various possible response structures
      const data = apiResponse.data;
      
      // Direct location result
      if (data.latitude && data.longitude) {
        console.log(`✅ Found location using ${searchType}: ${data.latitude}, ${data.longitude}`);
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          placeId: data.placeId,
          address: data.address || address,
          label: data.label || address,
        };
      }
      
      // Entries array
      if (data.entries && data.entries.length > 0) {
        const entry = data.entries[0];
        if (entry.latitude && entry.longitude) {
          console.log(`✅ Found location using ${searchType}: ${entry.latitude}, ${entry.longitude}`);
          return {
            latitude: entry.latitude,
            longitude: entry.longitude,
            placeId: entry.placeId,
            address: entry.address || address,
            label: entry.label || address,
          };
        }
      }
      
      // Results array
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        if (result.latitude && result.longitude) {
          console.log(`✅ Found location using ${searchType}: ${result.latitude}, ${result.longitude}`);
          return {
            latitude: result.latitude,
            longitude: result.longitude,
            placeId: result.placeId,
            address: result.address || address,
            label: result.label || address,
          };
        }
      }
      
      // If we got a successful response but no location data, continue to next search type
      continue;
    } catch (error) {
      console.warn(`❌ Error with internal ${searchType} search:`, error);
      continue; // Try next search type
    }
  }

  // If all methods failed, return null
  console.warn(`❌ No location found for address: ${address}`);
  return null;
}

/**
 * Search for a location (tries Google Maps first, then internal API)
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param address - Address to search for
 * @param serviceAreaCity - Service area city for validation
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to location search result or null
 */
export async function searchLocation(
  jwtToken: string,
  productId: string,
  networkId: string,
  address: string,
  serviceAreaCity: string,
  environment: Environment = 'UAT',
  detailedAddress?: string // Optional detailed address (like Python script uses)
): Promise<LocationSearchResult | null> {
  // Use detailed address if available, otherwise use basic address (matching Python script)
  const searchAddress = (detailedAddress && detailedAddress.trim()) ? detailedAddress : address;
  console.log(`🔍 Searching for location: ${searchAddress}`);
  if (detailedAddress && detailedAddress !== address) {
    console.log(`🔍 Using detailed address: ${detailedAddress}`);
  }
  
  try {
    const googleResult = await searchLocationWithGoogleMaps(
      searchAddress,
      serviceAreaCity
    );
    if (googleResult) {
      return googleResult;
    }
  } catch (error) {
    console.warn('[searchLocation] Google Maps proxy failed, falling back to internal API:', error);
  }

  // Fallback to internal API (this should work since it goes through proxy)
  console.log(`🔄 Falling back to internal API search for: ${address}`);
  return searchLocationWithInternalAPI(jwtToken, productId, networkId, searchAddress, environment);
}

/**
 * Create a ride in UAT/Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkId - Network ID (UUID string)
 * @param ridePayload - Ride creation payload
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to create ride response
 */
export async function createRide(
  jwtToken: string,
  productId: string,
  networkId: string,
  ridePayload: {
    departureAddress: {
      label: string;
      latitude: number;
      longitude: number;
      placeId: string | null;
    };
    arrivalAddress: {
      label: string;
      latitude: number;
      longitude: number;
      placeId: string | null;
    };
    time: string; // ISO 8601 UTC format (e.g., "2025-12-13T10:55:00.000Z")
    tripPassengers: Array<{
      id: string;
      seatType: string;
      concessionType: string;
      luggage: any[];
    }>;
    tripType: 'leaveAt' | 'arriveBy';
    userId: string;
    allowedServices?: string[]; // Optional: Array of service UUIDs
  },
  environment: Environment = 'UAT'
): Promise<CreateRideResponse> {
  const { opsCreateUrl } = getApiUrls(environment);
  
  // Debug: Log the exact URL being used
  console.log('[createRide] Full URL:', opsCreateUrl);
  console.log('[createRide] Environment:', environment);
  console.log('[createRide] Base URL:', getApiBaseUrl(environment));
  
  const payload: any = {
    type: 'createride',
    version: '1',
    aclConfig: {
      resource: 'rides',
      operation: 'create',
    },
    aclContext: 'operations',
    // Note: networkId is NOT included in payload - the working Python script doesn't include it
    arrivalAddress: ridePayload.arrivalAddress,
    departureAddress: ridePayload.departureAddress,
    returnTrip: null,
    time: ridePayload.time,
    tripPassengers: ridePayload.tripPassengers,
    tripType: ridePayload.tripType,
    userId: ridePayload.userId,
  };
  
  // Add allowedServices if provided (matching portal behavior)
  // Note: The portal includes allowedServices even in UAT, so we should include it if available
  if (ridePayload.allowedServices && ridePayload.allowedServices.length > 0) {
    payload.allowedServices = ridePayload.allowedServices;
  } else {
    // Log warning if allowedServices is missing - portal payloads include it
    console.warn('[createRide] allowedServices not provided - this may cause validation errors. Portal payloads include allowedServices.');
  }

  // Debug logging - log full request details with expanded objects
  console.log('[createRide] Request details:');
  console.log('URL:', opsCreateUrl);
  console.log('Method: POST');
  console.log('Environment:', environment);
  console.log('Network ID (not in payload):', networkId);
  console.log('Full payload:', JSON.stringify(payload, null, 2));
  
  // Verify URL construction matches Python script
  const expectedUrl = `${getApiBaseUrl(environment)}/context/ops/c`;
  if (opsCreateUrl !== expectedUrl) {
    console.error('[createRide] URL mismatch!', {
      expected: expectedUrl,
      actual: opsCreateUrl,
    });
  }

  const response = await makeRequestWithRetry(
    opsCreateUrl,
    'POST',
    createHeaders(jwtToken, productId, environment),
    undefined,
    payload,
    5,
    1000,
    environment
  );

  // Log the raw response immediately
  console.log('[createRide] Raw API response:', {
    status: response.status,
    hasData: !!response.data,
    dataType: typeof response.data,
    dataPreview: response.data ? JSON.stringify(response.data).substring(0, 500) : 'no data',
  });

  if (response.status !== 200) {
    // Enhanced error logging
    const errorData = typeof response.data === 'object' && response.data
      ? response.data
      : { status: response.status, message: `HTTP ${response.status}` };
    
    console.error('[createRide] API error:', {
      status: response.status,
      errorData: JSON.stringify(errorData, null, 2),
      url: opsCreateUrl,
      payloadType: payload.type,
      payloadTime: payload.time,
      fullPayload: JSON.stringify(payload, null, 2),
    });
    
    // Extract more detailed error message
    let errorMessage = errorData.message || errorData.error || `HTTP ${response.status}`;
    if (errorData.info) {
      errorMessage += ` - ${errorData.info}`;
    }
    // Include validation errors if present
    if (errorData.errors && Array.isArray(errorData.errors)) {
      errorMessage += ` - Errors: ${errorData.errors.join(', ')}`;
    }
    if (errorData.validationErrors) {
      errorMessage += ` - Validation: ${JSON.stringify(errorData.validationErrors)}`;
    }
    
    return {
      success: false,
      error: errorMessage,
    };
  }

  // Parse response
  const apiResponse = response.data;
  console.log('[createRide] API response:', {
    hasResponse: !!apiResponse,
    hasData: !!apiResponse?.data,
    responseStructure: JSON.stringify(apiResponse, null, 2),
  });
  
  if (!apiResponse || !apiResponse.data) {
    console.error('[createRide] Invalid response structure:', {
      apiResponse,
      responseData: response.data,
    });
    return {
      success: false,
      error: 'Invalid response structure',
    };
  }

  const data = apiResponse.data;
  if (!data.success) {
    // Extract all possible error details
    const errorDetails: any = {
      error: data.error,
      message: data.message,
      payload: data.payload,
    };
    
    // Check for validation errors in various possible locations
    if (data.validationErrors) errorDetails.validationErrors = data.validationErrors;
    if (data.errors) errorDetails.errors = data.errors;
    if (data.info) errorDetails.info = data.info;
    if (data.details) errorDetails.details = data.details;
    if (data.reason) errorDetails.reason = data.reason;
    
    // Log everything we can find - use console.dir for better object inspection
    console.error('[createRide] API returned success=false:');
    console.error('Error details:', errorDetails);
    console.error('Full data:', JSON.stringify(data, null, 2));
    console.error('Full response:', JSON.stringify(apiResponse, null, 2));
    console.error('Payload that was sent:', JSON.stringify(payload, null, 2));
    
    // Build a more detailed error message
    let errorMessage = data.error || data.message || 'Ride creation failed';
    if (data.info) errorMessage += ` - ${data.info}`;
    if (data.reason) errorMessage += ` - Reason: ${data.reason}`;
    if (data.validationErrors) errorMessage += ` - Validation: ${JSON.stringify(data.validationErrors)}`;
    if (data.errors && Array.isArray(data.errors)) errorMessage += ` - Errors: ${data.errors.join(', ')}`;
    
    return {
      success: false,
      error: errorMessage,
      message: data.message,
    };
  }

  // Extract ride ID
  const rideId = data.payload?.rideId;
  if (!rideId) {
    return {
      success: false,
      error: 'No ride ID in response',
    };
  }

  return {
    success: true,
    rideId,
  };
}

/**
 * Cancel/Delete ride response
 */
export interface CancelRideResponse {
  status: number;
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

/**
 * Cancel a ride in UAT/Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param networkUuid - Network UUID string
 * @param rideId - Ride ID to cancel
 * @param environment - Environment (UAT or Production)
 * @returns Promise resolving to cancel ride response
 */
export async function cancelRide(
  jwtToken: string,
  productId: string,
  networkUuid: string,
  rideId: string,
  environment: Environment = 'UAT'
): Promise<CancelRideResponse> {
  // Validate JWT token
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }

  // Validate rideId
  if (!rideId || !rideId.trim()) {
    throw new Error('Ride ID is required');
  }

  const { opsCreateUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  // Build cancellation payload matching API structure
  const payload = {
    type: 'cancelride',
    version: '1',
    aclConfig: {
      resource: 'rides',
      operation: 'update',
    },
    aclContext: 'operations',
    rideId: rideId.trim(),
  };

  // Log all headers being sent
  const headersObj: Record<string, string> = {};
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      headersObj[key] = key === 'authorization' ? `${value.substring(0, 20)}...` : value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      headersObj[key] = key === 'authorization' ? `${value.substring(0, 20)}...` : value;
    });
  } else {
    Object.entries(headers).forEach(([key, value]) => {
      headersObj[key] = key === 'authorization' ? `${String(value).substring(0, 20)}...` : String(value);
    });
  }

  console.log('[cancelRide] Cancelling ride:', {
    rideId,
    environment,
    url: opsCreateUrl,
    payload: JSON.stringify(payload, null, 2),
    headers: headersObj,
    networkUuid, // Log network UUID to verify it's being passed
  });

  const response = await makeRequestWithRetry(
    opsCreateUrl,
    'POST',
    headers,
    undefined,
    payload,
    5,
    1000,
    environment
  );

  // Parse response - following the same pattern as createRide
  // The API structure is: response.data = {status: 200, data: {success: true/false, ...}}
  const apiResponse = response.data;
  
  // Log the raw response immediately
  console.log('[cancelRide] Raw API response:', {
    status: response.status,
    hasData: !!apiResponse,
    dataType: typeof apiResponse,
    dataPreview: apiResponse ? JSON.stringify(apiResponse).substring(0, 500) : 'no data',
  });

  if (response.status !== 200) {
    const errorData = typeof apiResponse === 'object' && apiResponse
      ? apiResponse
      : { status: response.status, message: `HTTP ${response.status}` };
    
    console.error('[cancelRide] API error:', {
      status: response.status,
      errorData: JSON.stringify(errorData, null, 2),
    });
    
    return {
      status: response.status,
      success: false,
      error: errorData.message || errorData.error || `HTTP ${response.status}`,
      data: errorData,
    };
  }

  // Check response structure - same as createRide
  if (!apiResponse || !apiResponse.data) {
    console.error('[cancelRide] Invalid response structure:', {
      apiResponse,
      responseData: response.data,
    });
    return {
      status: response.status,
      success: false,
      error: 'Invalid response structure',
      data: apiResponse,
    };
  }

  // Extract the actual data object (same pattern as createRide)
  const data = apiResponse.data;
  const isSuccess = data.success === true;

  // Log full response structure for debugging
  console.log('[cancelRide] Parsed response:', {
    status: response.status,
    isSuccess,
    hasData: !!data,
    dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
    fullApiResponse: JSON.stringify(apiResponse, null, 2),
    fullData: JSON.stringify(data, null, 2),
  });

  if (isSuccess) {
    return {
      status: response.status,
      success: true,
      message: data.message || 'Ride cancelled successfully',
      data: data,
    };
  } else {
    // Extract error message - check multiple possible locations
    // Also check for nested error structures
    const errorMessage = 
      data.error || 
      data.message || 
      data.errorMessage ||
      data.reason ||
      data.info ||
      (data.errors && Array.isArray(data.errors) ? data.errors.join(', ') : null) ||
      (data.validationErrors ? JSON.stringify(data.validationErrors) : null) ||
      apiResponse.error ||
      apiResponse.message ||
      'Failed to cancel ride. The ride may already be cancelled, not found, or you may not have permission.';
    
    // Log full response for debugging - including all possible error fields
    console.error('[cancelRide] Cancel failed - Full diagnostic:', {
      status: response.status,
      isSuccess,
      errorMessage,
      dataKeys: Object.keys(data),
      dataValues: Object.entries(data).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v).substring(0, 100) : String(v).substring(0, 100)}`),
      data: JSON.stringify(data, null, 2),
      fullApiResponse: JSON.stringify(apiResponse, null, 2),
      requestPayload: JSON.stringify(payload, null, 2),
      rideId: rideId,
    });
    
    return {
      status: response.status,
      success: false,
      error: errorMessage,
      data: data,
    };
  }
}

